// record_toolbox_talk (PH or PA giver; WF-026, CORE-060/061). The giver records the
// talk and who attended; each attendee then signs their own understanding.
import { useId, useState } from 'react'
import { ErrorBanner } from '@/components/evidence'
import { Banner, Field, Spinner, TextArea, TextInput } from '@/components/ui'
import { TBT_TOPICS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { fromLocalInput, inputTimeArg, toLocalInput } from '@/lib/format'
import { useRegistry } from '@/lib/queries'
import { PermitWorkSummary } from '../briefing'
import { PanelFrame } from '../frame'
import { usePanelClock, usePanelCommand } from '../hooks'
import { cycleForAction, msToIso, revisionOf, tickProblems } from '../logic'
import { Choice, CycleHeader, DateTimeField, MissingCycle, roleLabel, SigningStatement, TickList, TickRow } from '../parts'

type GiverRole = 'PH' | 'PA'

export function ToolboxTalkPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cycle = cycleForAction(bundle, action)
  const now = usePanelClock(bundle)
  const registry = useRegistry()
  const run = usePanelCommand('record_toolbox_talk', bundle, onDone)
  const revision = revisionOf(bundle, cycle)
  const viewer = bundle.viewer.person
  const roleFromAction: GiverRole | null = action.role === 'PH' || action.role === 'PA' ? action.role : null

  const [giverRole, setGiverRole] = useState<GiverRole | null>(roleFromAction)
  // Exact default, sent with its seconds while the input shows that minute (see GasTestPanel).
  const [initialConductedAt] = useState(() => msToIso(now()))
  const [conductedAt, setConductedAt] = useState(() => toLocalInput(initialConductedAt))
  const [topics, setTopics] = useState<Record<string, boolean>>({})
  const [additional, setAdditional] = useState('')
  const [dra, setDra] = useState('')
  const [pause, setPause] = useState('')
  const [attendees, setAttendees] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [tried, setTried] = useState(false)
  const additionalId = useId()
  const draId = useId()
  const pauseId = useId()
  const searchId = useId()

  const people = [...(registry.data?.people ?? [])].sort((a, b) => a.display_name.localeCompare(b.display_name))
  const term = search.trim().toLowerCase()
  // Ticked people always stay visible, and only people still listed are sent.
  const chosen = attendees.filter((id) => people.some((p) => p.id === id))
  const shown = term ? people.filter((p) => p.display_name.toLowerCase().includes(term) || chosen.includes(p.id)) : people

  const problems: string[] = []
  if (!giverRole) problems.push('Choose whether you give the talk as PH or PA.')
  if (!fromLocalInput(conductedAt)) problems.push('Enter when the talk was given.')
  problems.push(...tickProblems(TBT_TOPICS, topics, 'topic'))
  if (!dra.trim()) problems.push('Enter the dynamic risk (DRA) plan.')
  if (chosen.length === 0) problems.push('Tick at least one attendee.')

  function submit() {
    setTried(true)
    if (!cycle || !giverRole || problems.length > 0) return
    void run.submit({
      permit_id: bundle.permit.id,
      cycle_id: cycle.id,
      giver_role: giverRole,
      reported_conducted_at: inputTimeArg(conductedAt, initialConductedAt) ?? '',
      content: {
        topics_covered: TBT_TOPICS.filter((t) => topics[t.code] === true).map((t) => t.code),
        additional_hazards: additional.trim(),
        dra_plan: dra.trim(),
        pause_and_check_points: pause.trim(),
      },
      attendee_person_ids: chosen,
    })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign toolbox talk"
      blockedNote={cycle ? null : 'Cycle not found.'}
    >
      {cycle ? <CycleHeader cycle={cycle} /> : <MissingCycle />}
      <p className="mb-4 text-sm text-ink">
        Give the talk at the worksite, after the Responsible Supervisor has validated the cycle and just before work starts, whatever the
        size of the job. Record it here, then each person signs their own understanding.
      </p>
      {cycle?.cycle_state === 'accepted' ? (
        <Banner tone="info" title="Work on this cycle has already started" className="mb-4">
          Record an additional talk for crew who join now. Each new person signs their own understanding before they start work.
          The earlier talk stays on the record.
        </Banner>
      ) : cycle && cycle.cycle_state !== 'validated' ? (
        <Banner tone="warn" title="This cycle is not validated yet" className="mb-4">
          The toolbox talk comes after the RS validation. Expect the server to refuse it until the cycle is validated.
        </Banner>
      ) : null}

      {roleFromAction ? (
        <p className="mb-4 text-sm text-ink">
          You give the talk as <strong>{roleLabel(roleFromAction)}</strong>.
        </p>
      ) : (
        <div className="mb-4">
          <Choice<GiverRole>
            legend="You give the talk as"
            name="tbt-giver-role"
            options={[
              { value: 'PH', label: 'Permit Holder' },
              { value: 'PA', label: 'Permit Applicant' },
            ]}
            value={giverRole}
            onChange={setGiverRole}
          />
        </div>
      )}

      <PermitWorkSummary revision={revision} open={false} />

      <DateTimeField label="When you gave the talk" required value={conductedAt} onChange={setConductedAt} />

      <TickList title="Topics you covered" ruleIds={['CORE-060']} options={TBT_TOPICS} value={topics} onChange={setTopics} />

      <Field
        label="Additional hazards and controls"
        htmlFor={additionalId}
        hint="Hazards found at the worksite that are not on the permit, and their controls. Write 'None' if there are none."
      >
        <TextArea id={additionalId} value={additional} onChange={(e) => setAdditional(e.target.value)} />
      </Field>
      <Field
        label="Dynamic risk (DRA) plan"
        required
        htmlFor={draId}
        hint="How the crew keeps checking for change: nearby work, weather, new hazards, crew or scope changes, detector failure (CORE-061)."
      >
        <TextArea id={draId} value={dra} onChange={(e) => setDra(e.target.value)} />
      </Field>
      <Field label="Pause and check points" htmlFor={pauseId} hint="When the crew stops to re-check, for example after each pipe support.">
        <TextArea id={pauseId} value={pause} onChange={(e) => setPause(e.target.value)} />
      </Field>

      <fieldset className="mb-4">
        <legend className="mb-1 text-sm font-semibold text-ink">
          Who attended <span className="text-bad" aria-hidden>*</span>
        </legend>
        <p className="mb-2 text-xs text-ink-soft">
          Tick everyone who attended. Each person signs on their own login; you cannot sign for them. The RS who authorised the
          permit cannot be in the work party (CORE-049). {chosen.length} ticked.
        </p>
        {registry.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-ink-soft">
            <Spinner small /> Loading people...
          </p>
        ) : registry.error ? (
          <ErrorBanner error={registry.error} title="Could not load the people list" />
        ) : (
          <>
            {people.length > 8 ? (
              <div className="mb-2">
                <label htmlFor={searchId} className="mb-1 block text-xs font-semibold text-ink-soft">
                  Find a person
                </label>
                <TextInput id={searchId} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type part of a name" />
              </div>
            ) : null}
            {shown.length === 0 ? (
              <p className="text-sm text-ink-soft">No one matches.</p>
            ) : (
              <div className="divide-y divide-line rounded-lg border border-line bg-white">
                {shown.map((p) => (
                  <TickRow
                    key={p.id}
                    checked={chosen.includes(p.id)}
                    onChange={(v) => setAttendees((prev) => (v ? [...prev, p.id] : prev.filter((id) => id !== p.id)))}
                    label={
                      <>
                        {p.display_name}
                        {p.id === viewer.id ? ' (you)' : ''}
                      </>
                    }
                    hint={[p.organisation, p.roles.length ? `Roles: ${p.roles.join(', ')}` : null].filter(Boolean).join(' - ') || undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </fieldset>

      {cycle && giverRole ? (
        <SigningStatement role={giverRole} person={viewer}>
          <p>
            I gave this toolbox talk at the worksite, just before the work, at the time shown. I covered every topic ticked above
            and answered the crew&apos;s questions.
          </p>
          <p>The people ticked attended. Each of them signs their own understanding; I do not sign for them.</p>
          <p>This talk is for cycle {cycle.cycle_number} only.</p>
        </SigningStatement>
      ) : null}
    </PanelFrame>
  )
}
